/**
 * Paced draws (2026-10-23, Chris: "people must be able to see each of their
 * draws"). After a pick the deck rests until that card's reveal panel has its
 * headline, the headline has stopped growing, and it has had `dwellMs` on
 * screen. Then the deck wakes for the next pick.
 *
 * Safety valves, so the deck can never be held for good:
 *   - no reveal panel within `noRevealMs` of the pick (a position the Mystic
 *     draws privately streams no chat panel at all),
 *   - a reveal held until the summary (`_holdForSummary`: not on screen),
 *   - `maxMs` after the pick, whatever is happening,
 *   - `release()`: the pick failed (ReaderPage's undoPick path).
 *
 * The pacer is plain state over an injected clock; ReaderPage polls `check`
 * with the chat's messages while it is active.
 */
export const PACE = Object.freeze({ dwellMs: 1500, noRevealMs: 3000, maxMs: 25000 })

const isReveal = (m) => !!(m && m.imageUrl)

/** The first card reveal at or after `from` in the chat, or null. */
export function revealSince (messages, from = 0) {
  if (!Array.isArray(messages)) return null
  for (let i = Math.max(0, from); i < messages.length; i++) {
    if (isReveal(messages[i])) return messages[i]
  }
  return null
}

/** "For the Past card, you selected …" → "Past" ('' when it is not that line). */
export function positionOf (preline) {
  const m = String(preline || '').match(/^\s*For (?:the )?(.+?) card, you selected/)
  return m ? m[1].trim() : ''
}

// "your Past card", but "the Heart of It card" for a position named with "The".
function cardPhrase (position) {
  const p = String(position || '').trim()
  if (!p) return 'your card'
  if (/^the\s+/i.test(p)) return `the ${p.replace(/^the\s+/i, '')} card`
  return `your ${p} card`
}

/** The reveal panel's waiting line: "Mystic Aida is reading your Past card". */
export function readingLine (mysticName, position) {
  const who = String(mysticName || '').trim() || 'The Mystic'
  return `${who} is reading ${cardPhrase(position)}`
}

/** The deck's line while it rests: "Reading your Past card…". */
export function restingLine (position) {
  const phrase = cardPhrase(position)
  return `Reading ${phrase}…`
}

export function revealPacer ({ pace = PACE, now = () => Date.now() } = {}) {
  let pick = null // { at, from, lastText, changedAt }

  const done = (reason, position = '') => { pick = null; return { resting: false, reason, position } }

  return {
    /** A pick just went out; `from` = the chat's length at the pick. */
    start (from) { pick = { at: now(), from: Number(from) || 0, lastText: null, changedAt: null } },
    release () { pick = null },
    active () { return !!pick },
    check (messages) {
      if (!pick) return { resting: false, reason: 'idle', position: '' }
      const t = now()
      const r = revealSince(messages, pick.from)
      const position = r ? positionOf(r.card) : ''
      if (t - pick.at >= pace.maxMs) return done('max', position)
      if (!r) {
        if (t - pick.at >= pace.noRevealMs) return done('no-reveal')
        return { resting: true, reason: 'waiting-reveal', position }
      }
      if (r._holdForSummary) return done('held', position)
      const text = String(r.text || '')
      if (!text.trim()) return { resting: true, reason: 'reading', position }
      if (text !== pick.lastText) { pick.lastText = text; pick.changedAt = t }
      if (t - pick.changedAt >= pace.dwellMs) return done('landed', position)
      return { resting: true, reason: 'dwell', position }
    },
  }
}
